<template>
  <div v-if="contents && parsedContentsPreview" class="space-y-8 pt-10">
    <img
      v-if="hub.homePageHeaderImageUrl"
      :src="getOptimizedImgUrl(hub.homePageHeaderImageUrl)"
      alt="Content preview image"
      class="rounded-md w-full aspect-[29/9] mx-auto object-cover"
    />
    <template v-if="contents.totalCount > 0">
      <HubNextContent
        :hub="hub"
        :next-content="parsedContentsPreview.nextContent"
      />
      <UpcomingSteps
        :contents="parsedContentsPreview.upcoming"
        @show-all="emit('hide-homepage')"
      />
    </template>
    <EmptyList v-else class="mt-15" />
  </div>
</template>
<script setup lang="ts">
import { apiGetContentsList } from '~/api/contents';
import { LaunchMetricType } from '~/api/metrics';
import HubNextContent from './_components/hub-next-content/HubNextContent.vue';
import UpcomingSteps from './_components/upcoming-steps/UpcomingSteps.vue';
import imageProxyBuilder, { ResizeType } from '@bitpatty/imgproxy-url-builder';
import { EmptyList } from '~/pages/_components/empty-list';
import { HubDto } from '~/api/hubs';
import { useAsyncData } from 'nuxt/app';

type Props = {
  hub: HubDto;
};

const props = defineProps<Props>();
const emit = defineEmits<{
  'hide-homepage': [];
}>();

const [{ data: contents }, { data: contentsPreviewList }] = await Promise.all([
  useAsyncData(`hub-homepage-${props.hub.slug}-contents`, () => {
    return apiGetContentsList(props.hub.slug, {
      page: 1,
      perPage: 1,
    });
  }),
  useAsyncData(`hub-homepage-${props.hub.slug}-preview-contents`, () => {
    return apiGetContentsList(props.hub.slug, {
      page: 1,
      perPage: 5,
      launchMetricTypes: [
        LaunchMetricType.NOT_STARTED,
        LaunchMetricType.OPENED,
      ],
    });
  }),
]);

const parsedContentsPreview = computed(() => {
  if (!contentsPreviewList.value) {
    return null;
  }
  return {
    nextContent: contentsPreviewList.value.nodes[0],
    upcoming: {
      totalCount: contentsPreviewList.value?.totalCount,
      nodes: contentsPreviewList.value?.nodes.slice(1),
    },
  };
});

const config = useRuntimeConfig();

const getOptimizedImgUrl = (url: string): string =>
  imageProxyBuilder()
    .resize({ type: ResizeType.FILL_DOWN, width: 1240, height: 381 })
    .format('webp')
    .build({
      baseUrl: config.public.imgProxyBaseUrl || '',
      path: url,
      addExtension: true,
    });
</script>
